import * as React from 'react'
import styled, { css } from 'styled-components'
import { ColorName, Colors, ColorsRGB, LightColors, DarkColors, Fonts } from '../constants'

type Size = 'small' | 'medium' | 'large'

export interface ButtonProps extends Omit<React.HTMLProps<HTMLButtonElement>, 'size'> {
  color?: ColorName
  filled?: boolean
  size?: Size
}

const computeFontSize = (size?: Size) => {
  if (size === 'small') {
    return 12
  } else {
    return 16
  }
}

const computeFontName = (size?: Size) => {
  if (size === 'small') {
    return Fonts.sansBody
  }
  return Fonts.sansHeader
}

const computePaddings = (size?: Size) => {
  if (size === 'small') {
    return '1px 6px 0 7px'
  } else if (size === 'medium') {
    return '8px 12px 8px 12px'
  } else {
    return '11px 24px 9px 24px'
  }
}

const computeBorderRadius = (size?: Size) => {
  if (size === 'small') {
    return 2
  }
  return 4
}

const computeLineHeight = (size?: Size) => {
  if (size === 'small') {
    return '1.5em'
  }
  return 'default'
}

const computeCursor = (size?: Size, onClick?: React.MouseEventHandler<HTMLButtonElement>) => {
  if (size === 'small') {
    return 'pointer'
  }
  return onClick == null ? 'default' : 'pointer'
}

const Button = styled.button<ButtonProps>`
  ${props => {
    const { color, filled, size, onClick } = props
    const fgName = color || 'sky'
    const fg = Colors[fgName]
    const fgRgb = ColorsRGB[fgName]
    const fgLight = LightColors[fgName]
    const fgDark = DarkColors[fgName]

    const colBorder = fg
    const col = filled ? Colors.ink : fg
    const colBg = filled ? fg : 'transparent'

    const fontSize = computeFontSize(size)
    const fontName = computeFontName(size)
    const paddings = computePaddings(size)
    const borderRadius = computeBorderRadius(size)
    const lineHeight = computeLineHeight(size)
    const cursor = computeCursor(size, onClick)

    return css`
      border: 1px solid ${colBorder};
      border-radius: ${borderRadius}px;
      color: ${col};
      background: ${colBg};
      font: ${fontSize}px ${fontName};
      line-height: ${lineHeight};
      padding: ${paddings};
      cursor: ${cursor};

      &:focus,
      &:hover {
        border-color: ${fgLight};
        color: ${filled ? Colors.ink : fgLight};
        background: ${filled ? fgLight : `rgba(${fgRgb.r}, ${fgRgb.g}, ${fgRgb.b}, 0.1)`};
      }

      &:active {
        color: ${filled ? Colors.ink : fgDark};
        border-color: ${fgDark};
        background: ${filled ? fgDark : Colors.black};
        opacity: ${filled ? 0.9 : 1};
      }

      &:disabled {
        opacity: 0.4;
        cursor: default;
        border-color: ${colBorder};
        color: ${col};
        background: ${colBg};
      }
    `
  }};
`

export default Button
